import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Card, CardContent, Divider, Grid, List, ListItem, Paper, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { blue, green, red, yellow } from '@mui/material/colors';
import AddIcon from '@mui/icons-material/Add';
import SecurityIcon from '@mui/icons-material/Security';
import { getInfo, getTransactions } from 'services/wesecure';
import CurrencyRupee from '@mui/icons-material/CurrencyRupee';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import NorthWestIcon from '@mui/icons-material/NorthWest';

import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import ScrollDialog from 'components/ScrollDialog';
import AmountDialog from './AmountDialog';

const WeSecure = () => {

  const gridStyle = useMemo(() => ({ minHeight: 380, width: '100%' }), []);
  const { enqueueSnackbar } = useSnackbar();
  const [infoData, setInfoData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  const columns = [
    { name: 'createdOn', header: 'Date', width: 110},
    { name: 'description', header: 'Description', width: 322},
    { name: 'openingBalance', header: 'Opening Balance', width: 160},
    { name: 'amount', header: 'Amount', width: 100},
    { name: 'closingBalance', header: 'Closing Balance', width: 150},
  ];

  useEffect(() => {
    getInitialData();
    return () => {}
  }, []);
  
  const getInitialData = useCallback(() => {
    getWeSecureInfo();
    getWeSecureTransactions();
  },[]);


  function getWeSecureInfo() {
    getInfo().then(({response, error}) => {
      if(response){
        setInfoData(response);
      } else {
        setInfoData(false);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function getWeSecureTransactions() {
    getTransactions().then(({response, error}) => {
      if(response){
        setTransactions(response);
      } else {
        setTransactions([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const onPaymentSuccess = useCallback((paymentResponse) => {
    setOpenModal(false);
    enqueueSnackbar("Your We Secure balance has been updated successfully", {variant: 'success'});
    getInitialData();
  },[enqueueSnackbar, getInitialData]);
  
  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        We Secure
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container columnSpacing={4} rowSpacing={4} >
          {infoData ? (
            <Grid container item xs={12} sm={12} md={12} xl={4} lg={4} justifyContent="center">
              <Grid item xs={12} sm={9} md={6} xl={12} lg={12} >
                <Card sx={{ minWidth: 275 }} >
                  <CardContent sx={{ backgroundColor: blue[100], textAlign: "center"}}>
                    <Typography component="p" variant="h3" >
                      <SecurityIcon sx={{fontSize: 36}}/>{infoData?.weSecureBalance}
                    </Typography>
                    <Typography variant="subtitle1" component="p" gutterBottom>
                      Your secure balance
                    </Typography>
                    <Button variant="contained" color="info" sx={{textTransform: 'none', mt: 1}} startIcon={<AddIcon />} onClick={() => setOpenModal(true)}>Topup Balance</Button>
                  </CardContent>
                  <CardContent>
                    <Typography component="p" variant="subtitle2" >
                      Recent transactions
                    </Typography>
                    <Divider  sx={{my:1}} />
                    {infoData?.recentTransactions?.length > 0 ? (
                      <List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
                        {infoData.recentTransactions.map((transaction, index) => (
                          <ListItem alignItems="flex-start" key={index}>
                            <Grid container spacing={2}>
                              <Grid item>
                                <Typography variant="body2" component="p" display="block" gutterBottom>
                                  {transaction.description}
                                </Typography>
                              </Grid>
                              <Grid item xs={4} sm={4} md={4} xl={4} lg={4} sx={{pt:'2px !important'}}>
                                <Typography variant="caption" component="p" display="block">
                                  Opening
                                </Typography>
                                <Typography variant="subtitle1" component="p" display="block"  sx={{fontWeight: 600}}>
                                  <CurrencyRupee sx={{verticalAlign: 'middle', fontSize:'1rem'}}/>{transaction.openingBalance}
                                </Typography>
                              </Grid>
                              <Grid item xs={4} sm={4} md={4} xl={4} lg={4} sx={{pt:'2px !important', color: transaction?.type === 'debit' ? red[600]: green[600]}}>
                                <Typography variant="caption" component="p" display="block">
                                  {transaction?.type === 'debit' ? 'Debited' : 'Credited'}
                                </Typography>
                                <Typography variant="subtitle1" component="p" display="block"  sx={{fontWeight: 600}}>
                                  {transaction?.type === 'debit' ? <NorthWestIcon sx={{verticalAlign: 'middle', fontSize:'1.01rem'}}/> : <SouthEastIcon sx={{verticalAlign: 'middle', fontSize:20}}/>}
                                  <CurrencyRupee sx={{verticalAlign: 'middle', fontSize:'1rem'}}/>{transaction.amount}
                                </Typography>
                              </Grid>
                              <Grid item xs={4} sm={4} md={4} xl={4} lg={4} sx={{pt:'2px !important', color: yellow[800]}}>
                                <Typography variant="caption" component="p" display="block">
                                  Closing
                                </Typography>
                                <Typography variant="subtitle1" component="p" display="block"  sx={{fontWeight: 600}}>
                                  <CurrencyRupee sx={{verticalAlign: 'middle', fontSize:'1rem'}}/>{transaction.closingBalance}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={12} md={12} xl={12} lg={12} sx={{pt:'0px !important', display: 'flex'}}  justifyContent="flex-end">
                                <Typography variant="caption"   display="block" gutterBottom >
                                  {transaction?.createdOn}
                                </Typography>
                              </Grid>
                            </Grid>
                          </ListItem>
                        ))}
                      </List>
                    ): null}
                  </CardContent>
                </Card>
              </Grid>
              <ScrollDialog
                title="Add Money"
                onModalClose={() => setOpenModal(false)}
                open={openModal}
                showCloseIcon={true}
                maxWidth="xs"
                disableDialogActions={true}
              >
                <AmountDialog onPaymentSuccess={onPaymentSuccess} />
              </ScrollDialog>
            </Grid>
          ): null}

          {transactions?.length > 0 ? (
            <Grid container item xs={12} sm={12} md={12} xl={8} lg={8}>
              <Paper component="div" sx={{ p:2,  width: '100%' }}>
                <Typography component="h6" variant="h6" gutterBottom>
                  All transactions
                </Typography>
                <ReactDataGrid
                  // handle={setGridRef}
                  // idProperty="index"
                  style={gridStyle}
                  defaultFilterValue={[]}
                  columns={columns}
                  dataSource={transactions}
                  rowHeight={null} 
                  minRowHeight={50}
                />
              </Paper>
            </Grid>
          ): null}
        </Grid>
      </Box>
    </Paper>
  );
}

export default React.memo(WeSecure);

